import { Component, ChangeDetectionStrategy, inject, signal, effect, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import {
  AlertComponent,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardGroupComponent,
  ColComponent,
  ContainerComponent,
  FormControlDirective,
  FormDirective,
  InputGroupComponent,
  InputGroupTextDirective,
  RowComponent,
  SpinnerComponent,
  TextColorDirective
} from '@coreui/angular-pro';
import { IconDirective, IconModule } from '@coreui/icons-angular';
import { cilEnvelopeClosed } from '@coreui/icons';
import { ENVIRONMENT, Logger, LogoComponent, UnassistedInputDirective } from 'shared';
import { AuthLanguageSwitcherComponent, TranslatePipe } from 'i18n';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-forgot-password-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    ContainerComponent,
    RowComponent,
    ColComponent,
    CardGroupComponent,
    TextColorDirective,
    CardComponent,
    CardBodyComponent,
    FormDirective,
    InputGroupComponent,
    InputGroupTextDirective,
    ButtonDirective,
    IconDirective,
    IconModule,
    SpinnerComponent,
    UnassistedInputDirective,
    AlertComponent,
    LogoComponent,
    TranslatePipe,
    AuthLanguageSwitcherComponent,
    FormControlDirective
  ],
  templateUrl: './forgot-password-page.component.html',
  styleUrls: ['./forgot-password-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ForgotPasswordPageComponent {
  private router = inject(Router);
  private http = inject(HttpClient);
  private environment: any = inject(ENVIRONMENT);
  private destroyRef = inject(DestroyRef);
  protected logger = new Logger('ForgotPasswordPageComponent');

  // ────────────────────────────────────────────────
  // Reactive state
  // ────────────────────────────────────────────────
  email = signal<string>('');
  isLoading = signal<boolean>(false);
  error = signal<string | null>(null);
  success = signal<boolean>(false);

  // ────────────────────────────────────────────────
  // Effects + Initialization
  // ────────────────────────────────────────────────
  constructor() {
  }

  // ────────────────────────────────────────────────
  // Actions
  // ────────────────────────────────────────────────
  requestReset(): void {
    const emailValue = this.email().trim();

    if (!emailValue) {
      this.error.set('Please enter your email address');
      return;
    }

    this.isLoading.set(true);
    this.error.set(null);

    this.http.post(`${this.environment.apiUrl}/auth/request_password_reset`, { email: emailValue })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.isLoading.set(false);
          this.success.set(true);
          this.logger.debug('Password reset request successful', response);
        },
        error: (err) => {
          this.isLoading.set(false);
          this.logger.error('Password reset request failed', err);
          this.error.set(err.error?.error || 'Failed to send reset email. Please try again.');
        }
      });
  }

  backToLogin(): void {
    this.router.navigate(['/login']);
  }
}